import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import ConsistencyLogo from '@/components/ConsistencyLogo';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/request-password-reset`,
        { email }
      );
      
      setSubmitted(true);
      
      // In dev/preview mode, backend returns token
      if (response.data.token) {
        setResetToken(response.data.token);
      }
      
      toast.success('Password reset instructions sent!');
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121417] p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 flex flex-col items-center">
            <ConsistencyLogo variant="full" size="lg" className="mb-4" />
          </div>

          <div className="glass-strong rounded-2xl p-8 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-green-500" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-[#8A93A6]">
                If an account exists with <strong className="text-white">{email}</strong>, 
                you will receive a password reset link.
              </p>
            </div>

            {/* Dev/Preview Mode: Show reset token */}
            {resetToken && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-xs text-yellow-400 font-semibold mb-2">🚧 DEV MODE ONLY</p>
                <p className="text-xs text-[#8A93A6] mb-3">Use this link to reset your password:</p>
                <Link 
                  to={`/reset-password?token=${resetToken}`}
                  className="block text-xs text-[#3A7BFF] hover:text-[#39E6C8] break-all"
                >
                  /reset-password?token={resetToken}
                </Link>
              </div>
            )}

            <Link 
              to="/login" 
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <ArrowLeft size={18} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#121417] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <ConsistencyLogo variant="full" size="lg" className="mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
          <p className="text-[#8A93A6]">Enter your email to receive a reset link</p>
        </div>

        <div className="glass-strong rounded-2xl p-8 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#8A93A6] mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-12"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <Link 
            to="/login" 
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
